import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Upload, LogOut } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { AdminPrestations } from "@/components/AdminPrestations";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 px-0 py-3 text-white placeholder:text-white/30 focus:border-gold focus:ring-0 focus:outline-none transition-colors duration-300";

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("imart_token", res.data.access_token);
      onSuccess();
      toast.success("Bienvenue au studio.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Connexion impossible."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <Reveal className="w-full max-w-md">
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-4">Espace artiste</p>
        <h1 className="font-serif text-4xl font-light mb-10">Connexion</h1>
        <form onSubmit={submit} className="space-y-8" data-testid="admin-login-form">
          <div>
            <label htmlFor="a-email" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1">Email</label>
            <input id="a-email" data-testid="admin-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.fr" className={inputCls} />
          </div>
          <div>
            <label htmlFor="a-pass" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1">Mot de passe</label>
            <input id="a-pass" data-testid="admin-password-input" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
          </div>
          <button type="submit" data-testid="admin-login-submit" disabled={loading}
            className="w-full bg-gold text-black py-4 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light disabled:opacity-50">
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
        <Link to="/" data-testid="admin-back-home" className="block mt-8 text-xs uppercase tracking-[0.15em] text-white/40 hover:text-gold transition-colors duration-300">
          ← Retour au site
        </Link>
      </Reveal>
    </div>
  );
};

const GalleryManager = ({ items, refresh, prestations }) => {
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [prestation, setPrestation] = useState("");
  const [uploading, setUploading] = useState(false);

  const prestationTitle = (slug) => prestations.find((p) => p.slug === slug)?.title || "";

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choisissez une photo ou une vidéo."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("prestation", prestation);
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Œuvre ajoutée à la galerie.");
      setTitle("");
      setPrestation("");
      fileRef.current.value = "";
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Échec du téléversement."));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Œuvre retirée.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <form onSubmit={upload} data-testid="upload-form"
        className="border border-white/15 p-8 mb-12 grid lg:grid-cols-4 gap-6 lg:items-end">
        <div>
          <label htmlFor="up-file" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
            Photo ou vidéo (200 Mo max)
          </label>
          <input id="up-file" ref={fileRef} data-testid="upload-file-input" type="file"
            accept="image/*,video/*" className="text-sm text-white/60 file:mr-4 file:py-2.5 file:px-5 file:border file:border-gold file:bg-transparent file:text-gold file:text-xs file:uppercase file:tracking-[0.15em] hover:file:bg-gold hover:file:text-black file:transition-colors file:duration-300" />
        </div>
        <div>
          <label htmlFor="up-title" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1">Titre (optionnel)</label>
          <input id="up-title" data-testid="upload-title-input" value={title}
            onChange={(e) => setTitle(e.target.value)} placeholder="Ex : Fresque — Lyon 2026" className={inputCls} />
        </div>
        <div>
          <label htmlFor="up-prestation" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1">Prestation liée</label>
          <select id="up-prestation" data-testid="upload-prestation-select" value={prestation}
            onChange={(e) => setPrestation(e.target.value)}
            className="w-full bg-transparent border-0 border-b border-white/20 px-0 py-3 text-white focus:border-gold focus:ring-0 focus:outline-none transition-colors duration-300 [&>option]:bg-[#121212]">
            <option value="">Aucune</option>
            {prestations.map((p) => (
              <option key={p.slug} value={p.slug}>{p.title}</option>
            ))}
          </select>
        </div>
        <button type="submit" data-testid="upload-submit-btn" disabled={uploading}
          className="inline-flex items-center justify-center gap-2 bg-gold text-black px-8 py-3.5 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light disabled:opacity-50">
          <Upload size={16} /> {uploading ? "Envoi…" : "Ajouter"}
        </button>
      </form>

      {items.length === 0 ? (
        <p className="text-white/40 py-10" data-testid="admin-gallery-empty">Aucune œuvre pour le moment. Ajoutez votre première photo ou vidéo.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map((item) => (
            <div key={item.id} data-testid={`admin-item-${item.id}`} className="border border-white/10 group">
              {item.type === "video" ? (
                <video src={mediaUrl(item.url)} muted playsInline preload="metadata" className="w-full aspect-square object-cover" />
              ) : (
                <img src={mediaUrl(item.url)} alt={item.title} className="w-full aspect-square object-cover" />
              )}
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-white/70 truncate">{item.title || "Sans titre"}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-white/30">
                    {item.type === "video" ? "Vidéo" : "Photo"}
                    {item.prestation && ` · ${prestationTitle(item.prestation)}`}
                  </p>
                </div>
                <button onClick={() => remove(item.id)} data-testid={`delete-item-${item.id}`} aria-label="Supprimer"
                  className="text-white/40 hover:text-neon-magenta transition-colors duration-300 shrink-0">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ListTable = ({ rows, columns, testid, empty }) => (
  rows.length === 0 ? (
    <p className="text-white/40 py-10" data-testid={`${testid}-empty`}>{empty}</p>
  ) : (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/15">
            {columns.map((c) => (
              <th key={c.key} className="text-left py-3 pr-6 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-300">
              {columns.map((c) => (
                <td key={c.key} className="py-4 pr-6 text-white/70 align-top max-w-xs truncate">{r[c.key] || "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("galerie");
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [prestations, setPrestations] = useState([]);

  const refresh = useCallback(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/prestations").then((r) => setPrestations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("imart_token");
    if (!token) { setAuthed(false); return; }
    api.get("/auth/me")
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem("imart_token"); setAuthed(false); });
  }, []);

  useEffect(() => { if (authed) refresh(); }, [authed, refresh]);

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center text-white/40 font-mono text-sm">Chargement…</div>;
  }
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  const logout = () => {
    localStorage.removeItem("imart_token");
    setAuthed(false);
  };

  const tabs = [
    { id: "galerie", label: `Galerie (${items.length})` },
    { id: "prestations", label: "Prestations" },
    { id: "demandes", label: `Demandes (${contacts.length})` },
    { id: "reservations", label: `Réservations (${bookings.length})` },
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 px-6 lg:px-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-3">Espace artiste</p>
          <h1 className="font-serif text-4xl lg:text-5xl font-light">Bonjour</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/" data-testid="admin-view-site" className="px-6 py-3 text-xs uppercase tracking-[0.15em] border border-white/20 text-white/60 hover:border-gold hover:text-gold transition-colors duration-300">
            Voir le site
          </Link>
          <button onClick={logout} data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.15em] border border-white/20 text-white/60 hover:border-neon-magenta hover:text-neon-magenta transition-colors duration-300">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-12">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-6 py-3 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
              tab === t.id ? "border-gold bg-gold text-black" : "border-white/20 text-white/60 hover:border-gold/60 hover:text-gold"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "galerie" && <GalleryManager items={items} refresh={refresh} prestations={prestations} />}
      {tab === "prestations" && <AdminPrestations />}
      {tab === "demandes" && (
        <ListTable testid="admin-contacts-table" rows={contacts} empty="Aucune demande pour le moment."
          columns={[
            { key: "created_at", label: "Reçue le" },
            { key: "name", label: "Nom" },
            { key: "email", label: "Email" },
            { key: "audience", label: "Profil" },
            { key: "service", label: "Prestation" },
            { key: "message", label: "Message" },
          ]} />
      )}
      {tab === "reservations" && (
        <ListTable testid="admin-bookings-table" rows={bookings} empty="Aucune réservation pour le moment."
          columns={[
            { key: "date", label: "Date" },
            { key: "slot", label: "Créneau" },
            { key: "name", label: "Nom" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Téléphone" },
            { key: "service", label: "Prestation" },
            { key: "status", label: "Statut" },
          ]} />
      )}
    </div>
  );
}
